/**
 * <cart-upsell> — the add-on row in the cart drawer and cart page.
 *
 * The snippet renders every eligible candidate (in stock, not already in the
 * cart) hidden; this picks which ones to show and adds them to the cart.
 *
 * The pick is seeded from a number kept in the visitor's own localStorage, so
 * the suggestions are stable for one person across page loads but differ from
 * shopper to shopper. Combined with the server-side filtering, what someone
 * sees depends on both who they are and what is already in their cart.
 */

import { fetchConfig } from '@theme/utilities';
import { CartAddEvent, ThemeEvents } from '@theme/events';

const SEED_KEY = 'oos:upsell-seed';

/**
 * A number that is stable for this browser and different between browsers.
 * @returns {number}
 */
function visitorSeed() {
  const fresh = () => Math.floor(Math.random() * 2 ** 31);

  try {
    const stored = Number(localStorage.getItem(SEED_KEY));

    if (Number.isFinite(stored) && stored > 0) return stored;

    const seed = fresh();
    localStorage.setItem(SEED_KEY, String(seed));
    return seed;
  } catch {
    // Private windows and blocked site data both throw; a seed that only lives
    // for this page load still gives a sensible spread.
    return fresh();
  }
}

/**
 * mulberry32 — a small seeded generator, so the same seed always produces the
 * same order.
 * @param {number} seed
 * @returns {() => number}
 */
function seededRandom(seed) {
  let state = seed >>> 0;

  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 2 ** 32;
  };
}

class CartUpsell extends HTMLElement {
  #controller = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    // Delegated, so the handler survives the section morph that follows every
    // cart change and replaces these list items.
    this.addEventListener('click', this.#onClick, { signal });

    // The morph can patch this element in place rather than reconnecting it, so
    // the pick is re-run explicitly once the new markup has landed.
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

    this.#pick();
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  #onCartUpdate = () => {
    requestAnimationFrame(() => this.#pick());
  };

  /** Shows `data-show` of the rendered candidates, chosen for this visitor. */
  #pick() {
    const items = [...this.querySelectorAll('[data-upsell-item]')];

    if (!items.length) {
      this.hidden = true;
      return;
    }

    const random = seededRandom(visitorSeed());
    const shuffled = items
      .map((element) => ({ element, key: random() }))
      .sort((a, b) => a.key - b.key);
    const show = Number(this.dataset.show) || 2;

    shuffled.forEach(({ element }, index) => {
      element.hidden = index >= show;
    });

    this.hidden = false;
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const button = /** @type {HTMLElement | null} */ (event.target instanceof Element ? event.target.closest('[data-upsell-add]') : null);

    if (button) this.#add(button);
  };

  /** @param {HTMLElement} button */
  async #add(button) {
    const variantId = button.dataset.variantId;

    if (!variantId || button.dataset.busy) return;

    button.dataset.busy = 'true';
    button.classList.add('cart-upsell__add--busy');

    const body = new FormData();
    body.append('id', variantId);
    body.append('quantity', '1');

    // Every cart-items-component on the page has to be named here, or the
    // response comes back without the HTML they need to re-render.
    const sectionIds = [
      ...new Set(
        [...document.querySelectorAll('cart-items-component')]
          .map((element) => /** @type {HTMLElement} */ (element).dataset.sectionId)
          .filter(Boolean)
      ),
    ];

    if (sectionIds.length) body.append('sections', sectionIds.join(','));

    const config = fetchConfig('javascript', { body });

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        ...config,
        headers: { ...config.headers, Accept: 'text/html' },
      });
      const result = await response.json();

      if (result.status) {
        button.classList.add('cart-upsell__add--error');
        return;
      }

      this.dispatchEvent(
        new CartAddEvent({}, 'cart-upsell', {
          source: 'cart-upsell',
          itemCount: 1,
          productId: button.dataset.productId,
          variantId,
          sections: result.sections,
        })
      );
    } catch (error) {
      console.error(error);
    } finally {
      button.classList.remove('cart-upsell__add--busy');
      delete button.dataset.busy;
    }
  }
}

if (!customElements.get('cart-upsell')) {
  customElements.define('cart-upsell', CartUpsell);
}
